import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import WalletManager from '../components/holder/WalletManager';
import HolderOverview from '../components/holder/HolderOverview';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Copy, ExternalLink, Download, Check, Search, RefreshCw, X } from 'lucide-react';
import { PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import FirstTimeChecklist from '../components/FirstTimeChecklist';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const COLORS = ['#47FFB0', '#4B9DFF', '#FFC107', '#FF6B6B'];

export default function BuyerDashboard({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [nfts, setNfts] = useState([]);
  const [holdings, setHoldings] = useState(null);
  const [payouts, setPayouts] = useState({ upcoming: [], history: [] });
  const [transfers, setTransfers] = useState([]);
  const [utilities, setUtilities] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedAddress, setCopiedAddress] = useState(null);
  // P3.2: Initialize search from URL params
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  // P8.0: Sync modal state
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncForm, setSyncForm] = useState({
    wallet: '', chain: 'ethereum', contract_address: '', token_standard: 'ERC721'
  });
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  // P3.2: Update search when URL params change
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchQuery(urlSearch);
    }
  }, [searchParams]);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [nftsRes, holdingsRes, payoutsRes, transfersRes, utilitiesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/buyer/nfts`, { headers }).catch(() => ({ data: { nfts: [] } })),
        axios.get(`${API}/buyer/holdings`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/buyer/payouts`, { headers }).catch(() => ({ data: { upcoming: [], history: [] } })),
        axios.get(`${API}/buyer/transfers`, { headers }).catch(() => ({ data: { transfers: [] } })),
        axios.get(`${API}/buyer/utilities`, { headers }).catch(() => ({ data: { utilities: [] } })),
        axios.get(`${API}/buyer/settings`, { headers }).catch(() => ({ data: null }))
      ]);

      setNfts(Array.isArray(nftsRes.data?.nfts) ? nftsRes.data.nfts : []);
      setHoldings(holdingsRes.data);
      const payoutsData = payoutsRes.data || {};
      setPayouts({
        upcoming: Array.isArray(payoutsData.upcoming) ? payoutsData.upcoming : [],
        history: Array.isArray(payoutsData.history) ? payoutsData.history : []
      });
      setTransfers(Array.isArray(transfersRes.data?.transfers) ? transfersRes.data.transfers : []);
      setUtilities(Array.isArray(utilitiesRes.data?.utilities) ? utilitiesRes.data.utilities : []);
      setSettings(settingsRes.data);
      // P8.0: Pre-fill sync form with primary wallet
      if (settingsRes.data?.primary_wallet) {
        setSyncForm(prev => ({ ...prev, wallet: settingsRes.data.primary_wallet }));
      }
    } catch (error) {
      console.error('Error fetching buyer data:', error);
      toast.error('Failed to load data');
      setNfts([]);
      setHoldings(null);
      setPayouts({ upcoming: [], history: [] });
      setTransfers([]);
      setUtilities([]);
      setSettings(null);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedAddress(label);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const exportPayoutsCSV = () => {
    const historyList = Array.isArray(payouts?.history) ? payouts.history : [];
    if (historyList.length === 0) {
      toast.error('No payout history to export');
      return;
    }
    const headers = ['Date', 'Portfolio', 'Amount', 'Method', 'Status', 'TX Hash'];
    const rows = historyList.map(p => [
      p.paid_date,
      p.portfolio_name,
      p.method === 'USD' ? `$${p.amount}` : p.method === 'Crypto' ? `${p.amount} ${p.crypto_token}` : `$${p.usd_amount} + ${p.crypto_amount} ${p.crypto_token}`,
      p.method,
      p.status,
      p.tx_hash || 'N/A'
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payouts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const getMethodBadge = (method) => {
    const styles = {
      USD: { bg: 'rgba(71, 255, 176, 0.1)', color: '#47FFB0' },
      Crypto: { bg: 'rgba(75, 157, 255, 0.1)', color: '#4B9DFF' },
      Split: { bg: 'rgba(255, 193, 7, 0.1)', color: '#FFC107' }
    };
    const style = styles[method] || styles.USD;
    return <span className="text-xs px-2 py-1 rounded font-semibold" style={{ background: style.bg, color: style.color }}>{method}</span>;
  };

  const getUnlockBadge = (utility) => {
    if (utility.unlock_status === 'unlocked') {
      return <span className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(71, 255, 176, 0.1)', color: '#47FFB0' }}>✓ Unlocked</span>;
    } else if (utility.unlock_status === 'time_locked') {
      return <span className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(255, 193, 7, 0.1)', color: '#FFC107' }}>🔒 {utility.unlock_date}</span>;
    } else if (utility.unlock_status === 'threshold_locked') {
      return <span className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(255, 107, 107, 0.1)', color: '#FF6B6B' }}>🔒 {utility.current_progress}</span>;
    }
  };

  // P3.0: Handle NFT card click
  const handleNFTClick = (nftId) => {
    navigate(`/buyer/nfts/${nftId}`);
  };

  // P8.0: Handle wallet sync
  const handleSync = async () => {
    setSyncing(true);
    setSyncError('');
    setSyncResult(null);
    try {
      const res = await axios.post(`${API}/buyer/wallet/sync`, {
        wallet: syncForm.wallet,
        chain: syncForm.chain,
        contract_address: syncForm.contract_address,
        token_standard: syncForm.token_standard
      });
      setSyncResult(res.data);
      toast.success(`Found ${res.data.found} tokens, ${res.data.upserted} new`);
      fetchAllData();
    } catch (err) {
      setSyncError(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFFFF' }}><div className="text-xl" style={{ color: '#0B0B0B' }}>Loading...</div></div>;
  }

  // Safe array accessors
  const safeNfts = Array.isArray(nfts) ? nfts : [];
  const safeUpcoming = Array.isArray(payouts?.upcoming) ? payouts.upcoming : [];
  const safeHistory = Array.isArray(payouts?.history) ? payouts.history : [];
  const safeTransfers = Array.isArray(transfers) ? transfers : [];
  const safeUtilities = Array.isArray(utilities) ? utilities : [];
  const safeWallets = Array.isArray(settings?.wallets) ? settings.wallets : [];

  // P3.0: Filter NFTs by search query
  const filteredNfts = safeNfts.filter(nft => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      nft.portfolio_name?.toLowerCase().includes(query) ||
      nft.serial?.toLowerCase().includes(query) ||
      nft.id?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6" style={{ color: '#0B0B0B', fontFamily: 'Space Grotesk' }}>Holder Dashboard</h1>
      <HolderOverview />

      <FirstTimeChecklist role="buyer" userId={user?.id} />

      <Tabs defaultValue="nfts" className="space-y-6">
        <TabsList className="bg-accent/20 border border-primary/20">
          <TabsTrigger value="nfts" data-testid="buyer-nfts-tab" style={{ color: '#0B0B0B' }}>NFT Records</TabsTrigger>
          <TabsTrigger value="holdings" data-testid="buyer-nav-marketplace" style={{ color: '#0B0B0B' }}>Holdings</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="buyer-nav-payouts" style={{ color: '#0B0B0B' }}>Payouts</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="buyer-nav-transfers" style={{ color: '#0B0B0B' }}>Transfers</TabsTrigger>
          <TabsTrigger value="utilities" data-testid="buyer-nav-utilities" style={{ color: '#0B0B0B' }}>Utilities</TabsTrigger>
          <TabsTrigger value="settings" data-testid="buyer-nav-settings" style={{ color: '#0B0B0B' }}>Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="nfts">
          {/* P3.0: Search bar + P8.0: Sync button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              background: '#FFFFFF', padding: '12px 16px', borderRadius: '12px',
              border: '1px solid #E6E8EF', flex: 1, maxWidth: '400px'
            }}>
              <Search size={20} color="#666" />
              <input
                type="text" placeholder="Search your NFTs..."
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="nft-search-input"
                style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '15px', color: '#0B0B0B', width: '100%' }}
              />
            </div>
            <button
              data-testid="sync-wallet-btn"
              onClick={() => setShowSyncModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '12px 20px', borderRadius: '12px',
                background: 'rgba(75, 157, 255, 0.1)', color: '#4B9DFF',
                border: '1px solid rgba(75, 157, 255, 0.3)', cursor: 'pointer',
                fontSize: '14px', fontWeight: 600
              }}
            >
              <RefreshCw size={16} /> Wallets & NFT check
            </button>
          </div>
          <div data-testid="nft-records-explainer" style={{ fontSize: 13, color: '#5B6472', marginBottom: 16 }}>
            These are NFT records on your account. Only items marked <b>Verified on-chain</b> are confirmed in one of your verified wallets and count toward NFT-only benefits. Claims waiting for an NFT appear in My Claims, not here.
          </div>

          {filteredNfts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {filteredNfts.map((nft, idx) => (
                <Card 
                  key={nft.id} 
                  className="glass-card border-primary/20 hover-lift cursor-pointer"
                  onClick={() => handleNFTClick(nft.id)}
                  data-testid={idx === 0 ? 'buyer-card-nft-0' : `nft-card-${nft.id}`}
                  style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(75, 157, 255, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <CardHeader className="p-0">
                    {nft.image_url ? (
                      <img src={nft.image_url} alt={nft.portfolio_name} className="w-full h-48 object-cover rounded-t-lg" />
                    ) : (
                      <div className="w-full h-48 rounded-t-lg flex items-center justify-center" style={{
                        background: nft.source === 'ONCHAIN' ? 'linear-gradient(135deg, #F1F3F7 0%, #F8F9FB 100%)' : '#FFFFFF',
                        borderBottom: '1px solid #333'
                      }}>
                        <span style={{ fontSize: '13px', color: '#5B6472' }}>No artwork provided</span>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-1" style={{ color: '#0B0B0B' }}>{nft.portfolio_name}</h3>
                    <p className="text-sm mb-1" style={{ color: '#5B6472' }}>
                      {nft.serial ? `Edition ${nft.serial}` : nft.token_id != null ? `Token ID ${nft.token_id}` : 'Token ID unknown'}
                    </p>
                    <p className="text-sm mb-3" style={{ color: '#5B6472' }}>
                      <span data-testid={`nft-ownership-badge-${nft.id}`} title={nft.ownership_note} style={{
                        padding: '1px 6px', borderRadius: '6px', fontSize: '10px', fontWeight: 700,
                        background: nft.ownership_status === 'verified_onchain' ? '#ECFDF5' : '#FFFBEB',
                        color: nft.ownership_status === 'verified_onchain' ? '#065F46' : '#92400E'
                      }}>{nft.ownership_label || 'Not verified'}</span>
                      {nft.utilities_count > 0 && (
                        <span style={{
                          marginLeft: '6px', padding: '1px 6px', borderRadius: '6px',
                          fontSize: '10px', fontWeight: 600,
                          background: 'rgba(71, 255, 176, 0.1)', color: '#47FFB0'
                        }}>{nft.utilities_count} utils</span>
                      )}
                    </p>
                    <p style={{ fontSize: 11, color: '#5B6472', marginBottom: 8 }}>{nft.ownership_note}</p>
                    <div className="flex gap-2 mb-2">
                      {nft.explorer_url && <button 
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(nft.contract_address, 'Contract'); }} 
                        className="px-2 py-1 rounded text-xs" 
                        style={{ background: 'rgba(75, 157, 255, 0.1)', color: '#4B9DFF' }}
                      >
                        {copiedAddress === 'Contract' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      </button>}
                      {nft.explorer_url && <button data-testid={`nft-explorer-${nft.id}`} title="View contract on Polygon Amoy explorer"
                        onClick={(e) => { e.stopPropagation(); window.open(nft.explorer_url, '_blank'); }} 
                        className="px-2 py-1 rounded text-xs" 
                        style={{ background: 'rgba(75, 157, 255, 0.1)', color: '#4B9DFF' }}
                      >
                        <ExternalLink className="w-3 h-3" />
                      </button>}
                    </div>
                    {nft.download_url ? (
                      <Button data-testid={`nft-download-${nft.id}`} className="w-full text-xs" title="Opens the artwork file attached to this record"
                        onClick={(e) => { e.stopPropagation(); window.open(nft.download_url, '_blank', 'noopener'); }}
                        style={{ background: 'rgba(71, 255, 176, 0.1)', color: '#065F46' }}>
                        <Download className="w-3 h-3 mr-1" /> Download artwork
                      </Button>
                    ) : <p data-testid={`nft-no-download-${nft.id}`} style={{ fontSize: 11, color: '#5B6472' }}>No downloadable artwork for this record.</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="glass-card border-primary/20">
              <CardContent className="pt-6 text-center">
                <p style={{ color: '#5B6472' }}>{searchQuery ? 'No NFT records match your search' : 'No NFT records yet. Claims without an issued NFT are listed in My Claims.'}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="holdings">
          {holdings ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="glass-card border-primary/20">
                <CardHeader><CardTitle style={{ color: '#4B9DFF' }}>Overview</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {holdings.total_value > 0 ? (<>
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Total Value</p><p className="text-3xl font-bold" style={{ color: '#47FFB0' }}>${holdings.total_value?.toLocaleString()}</p></div>
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Recorded change</p><p className="text-2xl font-bold" style={{ color: '#47FFB0' }}>{holdings.roi_percent || 0}%</p><p className="text-xs" style={{ color: '#5B6472' }}>Historical figure only. Not a promise of future returns.</p></div>
                    </>) : (
                    <p data-testid="holdings-no-activity" className="text-sm" style={{ color: '#5B6472' }}>No eligible purchase or payout activity yet, so there's nothing to calculate. Claims and account benefits don't appear here.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
              <Card className="glass-card border-primary/20">
                <CardHeader><CardTitle style={{ color: '#4B9DFF' }}>Allocation</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={holdings.allocation || []} cx="50%" cy="50%" label={({ name, percent }) => `${name}: ${percent}%`} outerRadius={70} dataKey="value">
                        {(holdings.allocation || []).map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card className="glass-card border-primary/20 lg:col-span-2">
                <CardHeader><CardTitle style={{ color: '#4B9DFF' }}>Payout Trend</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={holdings.recent_payouts || []}>
                      <XAxis dataKey="cycle" stroke="#5B6472" style={{ fontSize: '12px' }} />
                      <YAxis stroke="#5B6472" style={{ fontSize: '12px' }} />
                      <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #E6E8EF', borderRadius: '8px' }} />
                      <Line type="monotone" dataKey="amount" stroke="#47FFB0" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="glass-card border-primary/20">
              <CardContent className="pt-6 text-center">
                <p style={{ color: '#5B6472' }}>No holdings data available</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="payouts">
          <div className="flex justify-end mb-4">
            <Button onClick={exportPayoutsCSV} data-testid="export-csv-btn" style={{ background: '#4B9DFF', color: '#FFF' }}><Download className="w-4 h-4 mr-2" />Export CSV</Button>
          </div>
          <Card className="glass-card border-primary/20 mb-6">
            <CardHeader><CardTitle style={{ color: '#4B9DFF' }}>Upcoming Payouts</CardTitle></CardHeader>
            <CardContent>
              {safeUpcoming.length > 0 ? safeUpcoming.map((p) => (
                <div key={p.id} className="p-3 mb-2 rounded" style={{ background: 'rgba(75, 157, 255, 0.05)' }}>
                  <div className="flex justify-between">
                    <div><p style={{ color: '#0B0B0B' }}>{p.portfolio_name}</p><p className="text-sm" style={{ color: '#5B6472' }}>Due: {p.scheduled_date}</p></div>
                    <div className="text-right"><p style={{ color: '#47FFB0' }}>{p.method === 'USD' ? `$${p.amount}` : `${p.amount} ${p.token}`}</p>{getMethodBadge(p.method)}</div>
                  </div>
                </div>
              )) : (
                <p style={{ color: '#5B6472', textAlign: 'center', padding: '16px 0' }}>No upcoming payouts</p>
              )}
            </CardContent>
          </Card>
          <Card className="glass-card border-primary/20">
            <CardHeader><CardTitle style={{ color: '#4B9DFF' }}>History</CardTitle></CardHeader>
            <CardContent>
              {safeHistory.length > 0 ? safeHistory.map((p) => (
                <div key={p.id} className="p-3 mb-2 rounded" style={{ background: 'rgba(71, 255, 176, 0.05)' }}>
                  <div className="flex justify-between">
                    <div><p style={{ color: '#0B0B0B' }}>{p.portfolio_name}</p><p className="text-sm" style={{ color: '#5B6472' }}>{p.paid_date}</p></div>
                    <div className="text-right"><p style={{ color: '#47FFB0' }}>{p.method === 'USD' ? `$${p.amount}` : `${p.amount} ${p.crypto_token}`}</p>{getMethodBadge(p.method)}</div>
                  </div>
                </div>
              )) : (
                <p style={{ color: '#5B6472', textAlign: 'center', padding: '16px 0' }}>No payout history</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transfers">
          <Card className="glass-card border-primary/20">
            <CardHeader><CardTitle style={{ color: '#4B9DFF' }}>NFT Transfers</CardTitle></CardHeader>
            <CardContent>
              {safeTransfers.length > 0 ? safeTransfers.map((t) => (
                <div key={t.id} className="p-3 mb-2 rounded" style={{ background: 'rgba(75, 157, 255, 0.05)' }}>
                  <div className="flex justify-between">
                    <div><p style={{ color: '#0B0B0B' }}>{t.portfolio_name} #{t.serial}</p><p className="text-xs" style={{ color: '#5B6472' }}>{new Date(t.timestamp).toLocaleString()}</p></div>
                    <span className={`text-xs px-2 py-1 rounded ${t.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{t.status}</span>
                  </div>
                </div>
              )) : (
                <p style={{ color: '#5B6472', textAlign: 'center', padding: '16px 0' }}>No transfers yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="utilities">
          {safeUtilities.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {safeUtilities.map((u) => (
                <Card key={u.id} className="glass-card border-primary/20">
                  <CardHeader>
                    <div className="flex justify-between">
                      <div><CardTitle className="text-base" style={{ color: '#0B0B0B' }}>{u.title}</CardTitle><p className="text-xs" style={{ color: '#5B6472' }}>{u.portfolio_name}</p></div>
                      {getUnlockBadge(u)}
                    </div>
                  </CardHeader>
                  <CardContent><p className="text-sm" style={{ color: '#0B0B0B' }}>{u.description}</p></CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="glass-card border-primary/20">
              <CardContent className="pt-6 text-center">
                <p style={{ color: '#5B6472' }}>No utilities available</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="settings">
          {settings ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="glass-card border-primary/20">
                <CardHeader><CardTitle style={{ color: '#4B9DFF' }}>Profile</CardTitle></CardHeader>
                <CardContent>
                  <div><p className="text-sm" style={{ color: '#5B6472' }}>Name: {settings.profile?.display_name || 'Not set'}</p><p className="text-sm" style={{ color: '#5B6472' }}>Email: {settings.profile?.email || 'Not set'}</p></div>
                </CardContent>
              </Card>
              <Card className="glass-card border-primary/20">
                <CardHeader><CardTitle style={{ color: '#4B9DFF' }}>Wallets</CardTitle></CardHeader>
                <CardContent>
                  {safeWallets.length > 0 ? safeWallets.map((w) => (
                    <div key={w.id} className="p-2 rounded mb-2" style={{ background: 'rgba(75, 157, 255, 0.05)' }}>
                      <div className="flex justify-between">
                        <div><p className="text-xs" style={{ color: '#47FFB0' }}>{w.chain}</p><p className="text-xs" style={{ color: '#0B0B0B' }}>{w.address.substring(0, 10)}...{w.address.substring(w.address.length - 8)}</p></div>
                        <button onClick={() => copyToClipboard(w.address, 'Wallet')} className="px-2 py-1 rounded text-xs" style={{ background: 'rgba(75, 157, 255, 0.1)', color: '#4B9DFF' }}>
                          {copiedAddress === 'Wallet' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        </button>
                      </div>
                    </div>
                  )) : (
                    <p style={{ color: '#5B6472', textAlign: 'center', padding: '16px 0' }}>No wallets configured</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="glass-card border-primary/20">
              <CardContent className="pt-6 text-center">
                <p style={{ color: '#5B6472' }}>Settings not available</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* H2: Wallets & NFT check (auto-detects network/contract from your Kickbacks) */}
      {showSyncModal && (
        <div data-testid="sync-wallet-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setShowSyncModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 640, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button data-testid="sync-wallet-close" onClick={() => setShowSyncModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#5B6472' }}>Close</button>
            </div>
            <WalletManager />
          </div>
        </div>
      )}
    </div>
  );
}
